/**
 * Shared hero banner for Student Loan Contribution pages.
 *
 * Displays a gradient hero section with greeting, benefit progress tracker,
 * and metric cards. Supports four layout variants based on plan limits:
 *
 * - Variant 1: Annual limit — 4 equal cards (annual usage w/ progress, lifetime, last, next)
 * - Variant 2: Lifetime limit — 3 cards (wider lifetime usage w/ progress, last, next)
 * - Variant 3: No limit — 4 equal cards (year contribution, lifetime, last, next)
 * - Variant 4: Both limits — 4 equal cards (annual usage w/ progress, lifetime usage w/ progress, last, next)
 *
 * Each variant supports a blocked state where the Next Contribution card shows red text.
 */
import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { concat } from '@ember/helper';
import { t } from 'ember-intl';
import style from 'ember-style-modifier/modifiers/style';
import { eq, or } from 'tio-ui/utilities';
import Heading from 'tio-reimagined/components/heading';
import { formatCurrency } from 'tio-reimagined/format';
import {
  PhCalendarBlank,
  PhCheckCircle,
  PhPaperPlaneTilt,
  PhGift,
  PhMoneyWavy,
  PhProhibit,
  PhWarningCircle,
} from 'ember-phosphor-icons';

export interface ContributionHeroSignature {
  Args: {
    greeting: string;
    firstName: string;
    variant: 1 | 2 | 3 | 4;
    blocked?: boolean;
    annualUsage?: number;
    annualLimit?: number;
    annualProgressPercent?: number;
    lifetimeUsage?: number;
    lifetimeLimit?: number;
    lifetimeProgressPercent?: number;
    lifetimeContribution?: number;
    lifetimeAsOfDate?: string;
    currentYear?: number;
    yearContribution?: number;
    paymentCount?: number;
    lastPaymentAmount?: number;
    lastPaymentDate?: string;
    nextContribution: number;
    nextContributionDate: string;
    onViewBenefit?: () => void;
  };
}

export default class ContributionHero extends Component<ContributionHeroSignature> {
  handleViewBenefit = () => {
    this.args.onViewBenefit?.();
  };

  get isAnnualLimitReached() {
    return (this.args.annualProgressPercent ?? 0) >= 100;
  }

  get isLifetimeLimitReached() {
    return (this.args.lifetimeProgressPercent ?? 0) >= 100;
  }

  <template>
    <div class="hero-gradient-bg rounded-3xl p-6 relative overflow-hidden">
      {{! Decorative Circles }}
      <div
        class="absolute left-[64%] top-6.5 w-12 h-12 rounded-full pointer-events-none bg-purple-500/10 hero-float"
      ></div>
      <div
        class="absolute left-[66%] -top-14.5 w-30 h-30 rounded-full pointer-events-none bg-purple-500/5 hero-float"
        {{style animation-delay="-18s"}}
      ></div>
      <div
        class="absolute left-[77%] top-1.75 w-16 h-16 rounded-full pointer-events-none bg-purple-500/10 hero-float"
        {{style animation-delay="-35s"}}
      ></div>

      {{! Header with Greeting }}
      <div class="flex items-start justify-between mb-6">
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2 text-gray-600 text-base">
            <PhMoneyWavy @size={{20}} />
            <span class="font-medium">{{@greeting}}, {{@firstName}}</span>
          </div>
          <Heading @level={{2}} class="text-2xl font-semibold text-gray-900 leading-tight">
            {{t "slr.reimagined.your_student_loan_contributions"}}
          </Heading>
        </div>
        <button
          type="button"
          class="text-base font-semibold text-gray-900 hover:text-purple-700 underline decoration-solid"
          {{on "click" this.handleViewBenefit}}
        >
          {{t "slr.reimagined.benefit_details_link"}}
        </button>
      </div>

      {{! Metric Grid — layout determined by @variant }}
      {{#if (eq @variant 1)}}
        {{! Variant 1: Annual limit — 4 equal cards }}
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 xl:gap-6">
          {{! Annual Usage Card (with progress bar) }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[130px]">
            <div class="flex flex-col gap-3">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.annual_usage"}}
              </div>
              <div class="text-2xl font-semibold leading-none text-gray-900">
                {{formatCurrency (or @annualUsage 0)}}
              </div>
            </div>
            <div class="flex flex-col gap-1.5">
              <div
                class="h-2 bg-gray-900/16 rounded-2xl overflow-hidden"
                role="progressbar"
                aria-valuenow={{or @annualProgressPercent 0}}
                aria-valuemin={{0}}
                aria-valuemax={{100}}
                aria-label={{t "slr.reimagined.annual_usage"}}
              >
                <div
                  class="h-full bg-gray-900 rounded-2xl transition-all"
                  {{style width=(concat (or @annualProgressPercent 0) "%")}}
                ></div>
              </div>
              <div class="flex items-center gap-1.5">
                {{#if this.isAnnualLimitReached}}
                  <span class="text-sm font-medium leading-none text-gray-600">
                    {{t "slr.reimagined.limit_reached"}}
                  </span>
                  <PhProhibit @size={{16}} class="text-gray-600" />
                {{else}}
                  <span class="text-sm font-medium leading-none text-gray-600">
                    {{t "slr.reimagined.annual_limit"}}
                    {{formatCurrency (or @annualLimit 0)}}
                  </span>
                  <PhCalendarBlank @size={{16}} class="text-gray-600" />
                {{/if}}
              </div>
            </div>
          </div>

          {{! Lifetime Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[130px]">
            <div class="flex flex-col gap-3">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.lifetime_contribution"}}
              </div>
              <div class="text-[28px] font-semibold leading-none text-gray-900">
                {{formatCurrency (or @lifetimeContribution 0)}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium leading-none text-gray-600">
                {{#if @lifetimeAsOfDate}}
                  {{t "slr.reimagined.as_of_date" date=@lifetimeAsOfDate}}
                {{else}}
                  {{t "slr.reimagined.over_lifetime"}}
                {{/if}}
              </span>
              <PhGift @size={{16}} class="text-gray-600" />
            </div>
          </div>

          {{! Last Contribution Card }}
          {{#if @lastPaymentDate}}
            <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[130px]">
              <div class="flex flex-col gap-3">
                <div class="text-base font-medium leading-none text-gray-700">
                  {{t "slr.reimagined.last_contribution"}}
                </div>
                <div class="text-2xl font-semibold leading-none text-gray-900">
                  {{formatCurrency (or @lastPaymentAmount 0)}}
                </div>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-medium leading-none text-gray-600">
                  {{t "slr.reimagined.sent_date" date=@lastPaymentDate}}
                </span>
                <PhCheckCircle @size={{16}} class="text-gray-600" />
              </div>
            </div>
          {{/if}}

          {{! Next Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[130px]">
            <div class="flex flex-col gap-3">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.next_contribution"}}
              </div>
              <div
                class="text-2xl font-semibold leading-none
                  {{if @blocked 'text-red-800' 'text-gray-900'}}"
              >
                {{formatCurrency @nextContribution}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              {{#if @blocked}}
                <span class="text-sm font-medium leading-none text-red-800">
                  {{t "slr.reimagined.unable_to_process"}}
                </span>
                <PhWarningCircle @size={{16}} class="text-red-800" />
              {{else}}
                <span class="text-sm font-medium leading-none text-gray-600">
                  {{t "slr.reimagined.expected_date" date=@nextContributionDate}}
                </span>
                <PhPaperPlaneTilt @size={{16}} class="text-gray-600" />
              {{/if}}
            </div>
          </div>
        </div>

      {{else if (eq @variant 2)}}
        {{! Variant 2: Lifetime limit — wider first card + 2 standard cards }}
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 xl:gap-6">
          {{! Lifetime Benefit Usage Card (wider, with progress bar) }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[130px]">
            <div class="flex flex-col gap-3">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.lifetime_benefit_usage"}}
              </div>
              <div class="text-2xl font-semibold leading-none text-gray-900">
                {{formatCurrency (or @lifetimeUsage 0)}}
              </div>
            </div>
            <div class="flex flex-col gap-1.5">
              <div
                class="h-2 bg-gray-900/16 rounded-2xl overflow-hidden"
                role="progressbar"
                aria-valuenow={{or @lifetimeProgressPercent 0}}
                aria-valuemin={{0}}
                aria-valuemax={{100}}
                aria-label={{t "slr.reimagined.lifetime_benefit_usage"}}
              >
                <div
                  class="h-full bg-gray-900 rounded-2xl transition-all"
                  {{style width=(concat (or @lifetimeProgressPercent 0) "%")}}
                ></div>
              </div>
              <div class="flex items-center gap-1.5">
                {{#if this.isLifetimeLimitReached}}
                  <span class="text-sm font-medium leading-none text-gray-600">
                    {{t "slr.reimagined.limit_reached"}}
                  </span>
                  <PhProhibit @size={{16}} class="text-gray-600" />
                {{else}}
                  <span class="text-sm font-medium leading-none text-gray-600">
                    {{t "slr.reimagined.lifetime_limit"}}
                    {{formatCurrency (or @lifetimeLimit 0)}}
                  </span>
                  <PhGift @size={{16}} class="text-gray-600" />
                {{/if}}
              </div>
            </div>
          </div>

          {{! Last Contribution Card }}
          {{#if @lastPaymentDate}}
            <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[130px]">
              <div class="flex flex-col gap-3">
                <div class="text-base font-medium leading-none text-gray-700">
                  {{t "slr.reimagined.last_contribution"}}
                </div>
                <div class="text-2xl font-semibold leading-none text-gray-900">
                  {{formatCurrency (or @lastPaymentAmount 0)}}
                </div>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-medium leading-none text-gray-600">
                  {{t "slr.reimagined.sent_date" date=@lastPaymentDate}}
                </span>
                <PhCheckCircle @size={{16}} class="text-gray-600" />
              </div>
            </div>
          {{/if}}

          {{! Next Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[130px]">
            <div class="flex flex-col gap-3">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.next_contribution"}}
              </div>
              <div
                class="text-2xl font-semibold leading-none
                  {{if @blocked 'text-red-800' 'text-gray-900'}}"
              >
                {{formatCurrency @nextContribution}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              {{#if @blocked}}
                <span class="text-sm font-medium leading-none text-red-800">
                  {{t "slr.reimagined.unable_to_process"}}
                </span>
                <PhWarningCircle @size={{16}} class="text-red-800" />
              {{else}}
                <span class="text-sm font-medium leading-none text-gray-600">
                  {{t "slr.reimagined.expected_date" date=@nextContributionDate}}
                </span>
                <PhPaperPlaneTilt @size={{16}} class="text-gray-600" />
              {{/if}}
            </div>
          </div>
        </div>

      {{else if (eq @variant 3)}}
        {{! Variant 3: No limit — 4 equal cards }}
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 xl:gap-6">
          {{! Year Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[130px]">
            <div class="flex flex-col gap-3">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.year_contribution" year=@currentYear}}
              </div>
              <div class="text-2xl font-semibold leading-none text-gray-900">
                {{formatCurrency (or @yearContribution 0)}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium leading-none text-gray-600">
                {{@paymentCount}}
                {{t "slr.reimagined.contribution_count" count=@paymentCount}}
              </span>
              <PhMoneyWavy @size={{16}} class="text-gray-600" />
            </div>
          </div>

          {{! Lifetime Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[130px]">
            <div class="flex flex-col gap-3">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.lifetime_contribution"}}
              </div>
              <div class="text-[28px] font-semibold leading-none text-gray-900">
                {{formatCurrency (or @lifetimeContribution 0)}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium leading-none text-gray-600">
                {{#if @lifetimeAsOfDate}}
                  {{t "slr.reimagined.as_of_date" date=@lifetimeAsOfDate}}
                {{else}}
                  {{t "slr.reimagined.over_lifetime"}}
                {{/if}}
              </span>
              <PhGift @size={{16}} class="text-gray-600" />
            </div>
          </div>

          {{! Last Contribution Card }}
          {{#if @lastPaymentDate}}
            <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[130px]">
              <div class="flex flex-col gap-3">
                <div class="text-base font-medium leading-none text-gray-700">
                  {{t "slr.reimagined.last_contribution"}}
                </div>
                <div class="text-2xl font-semibold leading-none text-gray-900">
                  {{formatCurrency (or @lastPaymentAmount 0)}}
                </div>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-medium leading-none text-gray-600">
                  {{t "slr.reimagined.sent_date" date=@lastPaymentDate}}
                </span>
                <PhCheckCircle @size={{16}} class="text-gray-600" />
              </div>
            </div>
          {{/if}}

          {{! Next Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[130px]">
            <div class="flex flex-col gap-3">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.next_contribution"}}
              </div>
              <div
                class="text-2xl font-semibold leading-none
                  {{if @blocked 'text-red-800' 'text-gray-900'}}"
              >
                {{formatCurrency @nextContribution}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              {{#if @blocked}}
                <span class="text-sm font-medium leading-none text-red-800">
                  {{t "slr.reimagined.unable_to_process"}}
                </span>
                <PhWarningCircle @size={{16}} class="text-red-800" />
              {{else}}
                <span class="text-sm font-medium leading-none text-gray-600">
                  {{t "slr.reimagined.expected_date" date=@nextContributionDate}}
                </span>
                <PhPaperPlaneTilt @size={{16}} class="text-gray-600" />
              {{/if}}
            </div>
          </div>
        </div>

      {{else}}
        {{! Variant 4: Both limits — 4 equal cards, two with progress bars }}
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 xl:gap-6">
          {{! Annual Usage Card (with progress bar) }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[130px]">
            <div class="flex flex-col gap-3">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.annual_usage"}}
              </div>
              <div class="text-2xl font-semibold leading-none text-gray-900">
                {{formatCurrency (or @annualUsage 0)}}
              </div>
            </div>
            <div class="flex flex-col gap-1.5">
              <div
                class="h-2 bg-gray-900/16 rounded-2xl overflow-hidden"
                role="progressbar"
                aria-valuenow={{or @annualProgressPercent 0}}
                aria-valuemin={{0}}
                aria-valuemax={{100}}
                aria-label={{t "slr.reimagined.annual_usage"}}
              >
                <div
                  class="h-full bg-gray-900 rounded-2xl transition-all"
                  {{style width=(concat (or @annualProgressPercent 0) "%")}}
                ></div>
              </div>
              <div class="flex items-center gap-1.5">
                {{#if this.isAnnualLimitReached}}
                  <span class="text-sm font-medium leading-none text-gray-600">
                    {{t "slr.reimagined.limit_reached"}}
                  </span>
                  <PhProhibit @size={{16}} class="text-gray-600" />
                {{else}}
                  <span class="text-sm font-medium leading-none text-gray-600">
                    {{t "slr.reimagined.annual_limit"}}
                    {{formatCurrency (or @annualLimit 0)}}
                  </span>
                  <PhCalendarBlank @size={{16}} class="text-gray-600" />
                {{/if}}
              </div>
            </div>
          </div>

          {{! Lifetime Usage Card (with progress bar) }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[130px]">
            <div class="flex flex-col gap-3">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.lifetime_usage"}}
              </div>
              <div class="text-2xl font-semibold leading-none text-gray-900">
                {{formatCurrency (or @lifetimeUsage 0)}}
              </div>
            </div>
            <div class="flex flex-col gap-1.5">
              <div
                class="h-2 bg-gray-900/16 rounded-2xl overflow-hidden"
                role="progressbar"
                aria-valuenow={{or @lifetimeProgressPercent 0}}
                aria-valuemin={{0}}
                aria-valuemax={{100}}
                aria-label={{t "slr.reimagined.lifetime_usage"}}
              >
                <div
                  class="h-full bg-gray-900 rounded-2xl transition-all"
                  {{style width=(concat (or @lifetimeProgressPercent 0) "%")}}
                ></div>
              </div>
              <div class="flex items-center gap-1.5">
                {{#if this.isLifetimeLimitReached}}
                  <span class="text-sm font-medium leading-none text-gray-600">
                    {{t "slr.reimagined.limit_reached"}}
                  </span>
                  <PhProhibit @size={{16}} class="text-gray-600" />
                {{else}}
                  <span class="text-sm font-medium leading-none text-gray-600">
                    {{t "slr.reimagined.lifetime_limit"}}
                    {{formatCurrency (or @lifetimeLimit 0)}}
                  </span>
                  <PhCalendarBlank @size={{16}} class="text-gray-600" />
                {{/if}}
              </div>
            </div>
          </div>

          {{! Last Contribution Card }}
          {{#if @lastPaymentDate}}
            <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[130px]">
              <div class="flex flex-col gap-3">
                <div class="text-base font-medium leading-none text-gray-700">
                  {{t "slr.reimagined.last_contribution"}}
                </div>
                <div class="text-2xl font-semibold leading-none text-gray-900">
                  {{formatCurrency (or @lastPaymentAmount 0)}}
                </div>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-medium leading-none text-gray-600">
                  {{t "slr.reimagined.sent_date" date=@lastPaymentDate}}
                </span>
                <PhCheckCircle @size={{16}} class="text-gray-600" />
              </div>
            </div>
          {{/if}}

          {{! Next Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[130px]">
            <div class="flex flex-col gap-3">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.next_contribution"}}
              </div>
              <div
                class="text-2xl font-semibold leading-none
                  {{if @blocked 'text-red-800' 'text-gray-900'}}"
              >
                {{formatCurrency @nextContribution}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              {{#if @blocked}}
                <span class="text-sm font-medium leading-none text-red-800">
                  {{t "slr.reimagined.unable_to_process"}}
                </span>
                <PhWarningCircle @size={{16}} class="text-red-800" />
              {{else}}
                <span class="text-sm font-medium leading-none text-gray-600">
                  {{t "slr.reimagined.expected_date" date=@nextContributionDate}}
                </span>
                <PhPaperPlaneTilt @size={{16}} class="text-gray-600" />
              {{/if}}
            </div>
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
